"use client";

import { Heading, Image, Link, Text } from "@pamperedchef/design-system";

import * as styles from "./CategoryTiles.module.scss";

export interface CategoryTile {
	/** The category's authored code — stable, and unique within the grid. */
	code: string;
	name: string;
	href: string;
	image?: { src: string; alt: string };
	/** Live count of products in the category. Omit to render no count. */
	itemCount?: number;
}

export interface CategoryTilesProps {
	/** Authored on the layout. Defaults to the wording the design uses. */
	heading?: string;
	tiles: CategoryTile[];
	/** Off hides every count even where one was supplied. */
	showItemCounts?: boolean;
	/** Renders a labelled placeholder as the last cell when set. */
	trailingPlaceholder?: string;
}

const DEFAULT_HEADING = "Shop by Subcategory";

/**
 * The "Shop by Subcategory" grid. Receives finished hrefs and an already-ordered
 * list — ordering and link building belong to the caller, which is the only part
 * that knows the taxonomy.
 */
export function CategoryTiles({
	heading = DEFAULT_HEADING,
	tiles,
	showItemCounts = true,
	trailingPlaceholder
}: CategoryTilesProps): JSX.Element | null {
	if (tiles.length === 0) return null;

	return (
		<section className={styles["tiles"]} data-component="CategoryTiles">
			{heading ? (
				<Heading tag="h2" className={styles["tiles__heading"]}>
					{heading}
				</Heading>
			) : null}

			<ul className={styles["tiles__grid"]}>
				{tiles.map((tile) => (
					<li key={tile.code} className={styles["tile"]}>
						<a
							href={tile.href}
							aria-hidden="true"
							tabIndex={-1}
							className={styles["tile__image-link"]}
						>
							{tile.image ? (
								<Image
									src={tile.image.src}
									alt={tile.image.alt}
									className={styles["tile__image"]}
								/>
							) : (
								<span className={styles["tile__placeholder"]} />
							)}
						</a>

						<Link href={tile.href} className={styles["tile__name"]}>
							{tile.name}
						</Link>

						{showItemCounts && tile.itemCount !== undefined ? (
							<Text tag="p" className={styles["tile__count"]}>
								{tile.itemCount} items
							</Text>
						) : null}
					</li>
				))}

				{trailingPlaceholder ? (
					<li
						className={styles["tile"]}
						data-placeholder={trailingPlaceholder}
					>
						<span className={styles["tile__stub"]}>
							<Text tag="span" className={styles["tile__name"]}>
								{trailingPlaceholder}
							</Text>
						</span>
					</li>
				) : null}
			</ul>
		</section>
	);
}
